import { post, putBlobPublic, BASE } from '../api.js';
import { initWasm, getWasm, padPlaintext } from '../crypto.js';
import { lucideIcons, esc } from '../app.js';
import { humanSize } from '../utils.js';
import { toast } from '../dialog.js';

// serde_wasm_bindgen returns Map objects; convert to plain objects
function obj(v) {
  if (v instanceof Map) return Object.fromEntries(v);
  return v;
}

// V1 format without AAD — always writes V1 (0x01 prefix).
function encryptAndConcat(wasm, key, data) {
  const aadBytes = new Uint8Array(0);
  const p = obj(wasm.encrypt_v1(key, data, aadBytes));
  const ct = new Uint8Array(p.ciphertext);
  const n = new Uint8Array(p.nonce);
  const r = new Uint8Array(1 + n.length + ct.length);
  r[0] = 0x01;
  r.set(n, 1);
  r.set(ct, 1 + n.length);
  return r;
}

export function renderDeadDropSend(container, params) {
  const idOrSlug = params.id_or_slug;

  container.innerHTML = `
    <div style="max-width:520px;margin:40px auto;padding:0 16px">
      <div style="text-align:center;margin-bottom:24px">
        <div style="display:inline-flex;align-items:center;gap:10px;margin-bottom:8px">
          <svg viewBox="0 0 32 32" width="32" height="32"><defs><linearGradient id="bk-inbox" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-inbox)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-inbox)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-inbox)" stroke-width="1.5" stroke-linecap="round"/></svg>
          <span style="font-size:20px;font-weight:700;color:var(--text-primary,#fff)">Blind<span style="color:#3ecfb4">Keep</span></span>
        </div>
        <h2 style="margin:0 0 4px;font-size:22px;color:var(--text-primary,#fff)">Secure Inbox</h2>
        <div id="inbox-label"></div>
        <p style="color:var(--text-secondary,#8b8fa3);font-size:14px;margin:0">Send an encrypted file. Only the inbox owner can decrypt it.</p>
      </div>
      <div id="inbox-content"><p class="loading">Loading inbox...</p></div>
    </div>
  `;

  loadInbox(idOrSlug);
}

async function loadInbox(idOrSlug) {
  const content = document.getElementById('inbox-content');
  if (!content) return;

  try {
    const res = await fetch(`${BASE}/dead-drop-inboxes/${encodeURIComponent(idOrSlug)}/info`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      content.innerHTML = `<div class="msg msg-error">${esc(data.error || 'This inbox is not available.')}</div>`;
      return;
    }
    const info = await res.json();
    if (info.label) {
      const labelEl = document.getElementById('inbox-label');
      if (labelEl) labelEl.innerHTML = `<p style="color:var(--accent,#3ecfb4);font-size:15px;font-weight:600;margin:0 0 4px">${esc(info.label)}</p>`;
    }
    showFilePicker(content, idOrSlug, info);
  } catch (err) {
    content.innerHTML = `<div class="msg msg-error">Failed to load inbox: ${esc(err.message)}</div>`;
  }
}

function showFilePicker(content, idOrSlug, info) {
  content.innerHTML = `
    <div class="landing-drop" id="inboxDrop" style="margin-bottom:16px">
      <div class="drop-icon"><i data-lucide="upload-cloud" style="width:28px;height:28px"></i></div>
      <p class="drop-desktop-hint">Drop a file here or click to upload</p>
      <label for="inboxFileInput" class="btn btn-primary drop-mobile-btn"><i data-lucide="file-up" style="width:18px;height:18px"></i> Choose File</label>
      <span>Max 100 MB &middot; Encrypted in your browser</span>
      <input type="file" id="inboxFileInput" style="display:none">
    </div>
    <p style="font-size:12px;color:var(--text-secondary,#8b8fa3);text-align:center">
      <i data-lucide="shield-check" style="width:12px;height:12px;vertical-align:middle"></i>
      End-to-end encrypted with X25519 + XChaCha20-Poly1305
    </p>
  `;
  lucideIcons();

  const dz = document.getElementById('inboxDrop');
  const fi = document.getElementById('inboxFileInput');

  dz.addEventListener('click', (e) => {
    if (e.target === fi || e.target.closest('.drop-mobile-btn')) return;
    fi.click();
  });
  dz.addEventListener('dragover', (e) => { e.preventDefault(); dz.classList.add('dragover'); });
  dz.addEventListener('dragleave', () => dz.classList.remove('dragover'));
  dz.addEventListener('drop', (e) => {
    e.preventDefault();
    dz.classList.remove('dragover');
    if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0], content, idOrSlug, info);
  });
  fi.addEventListener('change', () => { if (fi.files.length) handleFile(fi.files[0], content, idOrSlug, info); });
}

async function handleFile(file, content, idOrSlug, info) {
  const overlay = document.createElement('div');
  overlay.className = 'encrypt-overlay';
  overlay.innerHTML = `<div class="encrypt-box"><div class="spinner"></div><p>Encrypting ${esc(file.name)}...</p><div class="mono">XChaCha20-Poly1305</div></div>`;
  document.body.appendChild(overlay);

  try {
    await initWasm();
    const wasm = getWasm();
    const ownerPubkey = new Uint8Array(info.public_key);

    const bytes = new Uint8Array(await file.arrayBuffer());

    // Build envelope: [4-byte BE meta_len][JSON metadata][file bytes]
    const meta = JSON.stringify({ name: file.name, type: file.type, size: file.size });
    const metaBytes = new TextEncoder().encode(meta);
    const header = new Uint8Array(4);
    new DataView(header.buffer).setUint32(0, metaBytes.length);
    const payload = new Uint8Array(4 + metaBytes.length + bytes.length);
    payload.set(header);
    payload.set(metaBytes, 4);
    payload.set(bytes, 4 + metaBytes.length);

    // Generate random item key
    const itemKey = new Uint8Array(wasm.generate_random_key());

    // Pad and encrypt with V1 (empty AAD) for compatibility with decrypt_auto fallback
    const padded = padPlaintext(payload);
    const encryptedBlob = encryptAndConcat(wasm, itemKey, padded);

    // Wrap item key for owner using X25519
    const result = obj(wasm.wrap_key_for_recipient_v1(itemKey, ownerPubkey));
    const wrappedKey = new Uint8Array(result.wrapped_key);
    const ephPubkey = new Uint8Array(result.ephemeral_pubkey);
    const wrapNonce = new Uint8Array(result.nonce);

    // Concatenate: eph_pubkey(32) + nonce(24) + wrapped_key(49) = 105 bytes
    const concat105 = new Uint8Array(32 + 24 + wrappedKey.length);
    concat105.set(ephPubkey, 0);
    concat105.set(wrapNonce, 32);
    concat105.set(wrappedKey, 56);

    // Get blob nonce from V1 format: 0x01 || nonce(24) || ciphertext
    const blobNonce = encryptedBlob.slice(1, 25);

    overlay.querySelector('p').textContent = 'Uploading...';

    // Request presigned upload URL
    const urlResp = await (await fetch(`${BASE}/dead-drop-inboxes/${encodeURIComponent(idOrSlug)}/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ size_bytes: encryptedBlob.byteLength }),
    })).json();

    if (urlResp.error) throw new Error(urlResp.error);

    // Upload encrypted blob: try direct S3, fall back to API proxy
    let directFailed = false;
    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 30000;
        xhr.open('PUT', urlResp.upload_url);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            overlay.querySelector('p').textContent = `Uploading... ${pct}%`;
          }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => { directFailed = true; reject(new Error('Upload failed')); };
        xhr.ontimeout = () => { directFailed = true; reject(new Error('Timeout')); };
        xhr.send(encryptedBlob);
      });
    } catch (err) {
      if (!directFailed || !urlResp.proxy_upload) throw err;
      overlay.querySelector('p').textContent = 'Uploading via relay...';
      await putBlobPublic(`/dead-drop-inboxes/${encodeURIComponent(idOrSlug)}/upload/${urlResp.s3_key}`, encryptedBlob);
    }

    overlay.querySelector('p').textContent = 'Finalizing...';

    // Complete the upload — register as item
    const completeResp = await (await fetch(`${BASE}/dead-drop-inboxes/${encodeURIComponent(idOrSlug)}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        s3_key: urlResp.s3_key,
        wrapped_key: Array.from(concat105),
        nonce: Array.from(blobNonce),
        size_bytes: encryptedBlob.byteLength,
      }),
    })).json();

    if (completeResp.error) throw new Error(completeResp.error);

    overlay.remove();
    showSuccess(content, file.name, file.size, idOrSlug);
  } catch (err) {
    overlay.remove();
    toast('Upload failed: ' + err.message);
  }
}

function showSuccess(content, filename, fileSize, idOrSlug) {
  content.innerHTML = `
    <div style="text-align:center">
      <div class="check-circle check-pop" style="margin:0 auto 16px"><i data-lucide="check" style="width:28px;height:28px"></i></div>
      <h3 style="margin:0 0 8px;color:var(--text-primary,#fff)">File Delivered Securely</h3>
      <p style="color:var(--text-secondary,#8b8fa3);font-size:14px;margin:0 0 16px">
        Only the inbox owner can decrypt it.
      </p>
      <div class="card" style="text-align:left;margin-bottom:16px">
        <div class="detail-label">File</div>
        <div class="detail-value">${esc(filename)}</div>
        <div class="detail-label">Size</div>
        <div class="detail-value">${humanSize(fileSize)}</div>
      </div>
      <button class="btn btn-primary btn-sm" id="sendAnotherBtn">Send another file</button>
    </div>
  `;
  lucideIcons();

  document.getElementById('sendAnotherBtn').onclick = () => {
    loadInbox(idOrSlug);
  };
}
